import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { useOnboarding } from '../../../../context/OnboardingContext';
import { PrivateWizardLayout, WizardContainer, WizardCard, WizardActions } from '../../WizardLayout';
import Stepper from '../../Stepper';
import { toast } from 'react-toast';
import '../../../../styles/wizard.css';

const Step10IotValidation = () => {
  const navigate = useNavigate();
  const { iot, setIot, nextStep, previousStep,markStepCompleted,goToStep  } = useOnboarding();
  
  const [evidenceSettings, setEvidenceSettings] = useState(iot.evidence || {
    captureLogs: true,
    captureTelemetry: true,
    captureVideo: false,
    captureSnapshots: true
  });

  const handleToggle = (setting) => {
    setEvidenceSettings({
      ...evidenceSettings,
      [setting]: !evidenceSettings[setting]
    });
  };

  const handleContinue = () => {
    // Check if at least one evidence type is selected
    const hasEvidence = Object.values(evidenceSettings).some(val => val === true);
    
    if (!hasEvidence) {
      toast.error('Please enable at least one evidence capture option');
      return;
    }

    // Save evidence settings
    setIot({
      ...iot,
      evidence: evidenceSettings
    });

    markStepCompleted(10);
    goToStep(11);
    toast.success('Evidence settings saved');
    navigate('/app/onboarding?step=11');
  };

  const evidenceOptions = [
    {
      key: 'captureLogs',
      icon: '📝',
      title: 'Capture Logs',
      description: 'Record detailed execution logs and timestamps',
      recommended: true
    },
    {
      key: 'captureTelemetry',
      icon: '📊',
      title: 'Capture Telemetry',
      description: 'Collect device metrics, battery, signal strength',
      recommended: true
    },
    {
      key: 'captureSnapshots',
      icon: '📸',
      title: 'Capture Snapshots',
      description: 'Take screenshots at key test points',
      recommended: true
    },
    {
      key: 'captureVideo',
      icon: '🎥',
      title: 'Capture Video',
      description: 'Record full video of test execution',
      recommended: false,
      note: 'May increase storage requirements'
    }
  ];

  return (
    <PrivateWizardLayout>
      <WizardContainer>
        <Stepper currentStep={10} completedSteps={[1, 2, 3, 4, 5, 6, 7, 8, 9]} />
        
        <WizardCard
          title="Validation & Evidence Settings"
          subtitle="Configure what evidence to capture during test execution"
        >
          {/* Evidence capture options */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1.5rem' }}>
              Evidence Capture
            </h3>

            <div style={{ display: 'grid', gap: '1rem' }}>
              {evidenceOptions.map(option => (
                <div
                  key={option.key}
                  onClick={() => handleToggle(option.key)}
                  style={{
                    padding: '1.5rem',
                    border: `2px solid ${evidenceSettings[option.key] ? 'var(--primary-color)' : 'var(--border-color)'}`,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    background: evidenceSettings[option.key] ? 'rgba(67, 97, 238, 0.05)' : 'transparent',
                    position: 'relative'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'start', gap: '1rem' }}>
                    {/* Icon */}
                    <div style={{
                      fontSize: '2rem',
                      flexShrink: 0
                    }}>
                      {option.icon}
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        marginBottom: '0.5rem'
                      }}>
                        <h4 style={{
                          margin: 0,
                          fontSize: '1.125rem',
                          fontWeight: '600'
                        }}>
                          {option.title}
                        </h4>
                        {option.recommended && (
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            background: 'var(--success-color)',
                            color: 'white',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: '600'
                          }}>
                            RECOMMENDED
                          </span>
                        )}
                      </div>
                      <p style={{
                        margin: 0,
                        color: 'var(--text-secondary)',
                        fontSize: '0.875rem'
                      }}>
                        {option.description}
                      </p>
                      {option.note && (
                        <p style={{
                          margin: '0.5rem 0 0 0',
                          color: 'var(--warning-color)',
                          fontSize: '0.75rem',
                          fontStyle: 'italic'
                        }}>
                          ⚠ {option.note}
                        </p>
                      )}
                    </div>

                    {/* Toggle */}
                    <div style={{
                      width: '52px',
                      height: '28px',
                      background: evidenceSettings[option.key] ? 'var(--primary-color)' : '#ccc',
                      borderRadius: '14px',
                      position: 'relative',
                      transition: 'all 0.3s ease',
                      flexShrink: 0
                    }}>
                      <div style={{
                        width: '22px',
                        height: '22px',
                        background: 'white',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: '3px',
                        left: evidenceSettings[option.key] ? '27px' : '3px',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div style={{
            padding: '1.5rem',
            background: 'var(--bg-gray-50)',
            borderRadius: '12px',
            border: '1px solid var(--border-color)'
          }}>
            <h4 style={{
              fontSize: '1rem',
              fontWeight: '600',
              marginBottom: '1rem'
            }}>
              Evidence Summary
            </h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem'
            }}>
              {evidenceOptions.map(option => (
                <div
                  key={option.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem',
                    background: 'white',
                    borderRadius: '6px',
                    opacity: evidenceSettings[option.key] ? 1 : 0.5
                  }}
                >
                  <span style={{ fontSize: '1.25rem' }}>
                    {evidenceSettings[option.key] ? '✓' : '○'}
                  </span>
                  <span style={{
                    fontSize: '0.875rem',
                    color: evidenceSettings[option.key] ? 'var(--text-primary)' : 'var(--text-secondary)'
                  }}>
                    {option.title.replace('Capture ', '')}
                  </span>
                </div>
              ))}
            </div>

            <div style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: 'rgba(67, 97, 238, 0.1)',
              borderRadius: '6px',
              fontSize: '0.875rem',
              color: 'var(--text-secondary)'
            }}>
              💡 <strong>Tip:</strong> More evidence types provide better debugging capabilities
              but may increase storage usage and test execution time.
            </div>
          </div>

          {/* Validation info box */}
          <div style={{
            marginTop: '2rem',
            padding: '1.5rem',
            background: 'rgba(76, 201, 240, 0.1)',
            border: '1px solid rgba(76, 201, 240, 0.3)',
            borderRadius: '12px'
          }}>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <span style={{ fontSize: '2rem',  flexShrink: 0 }}>ℹ️</span>
              <div>
                <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600' }}>
                  Validation Rules
                </h4>
                <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                  Validation rules were configured in Step 8 during interaction setup.
                  The system will use those rules along with the evidence captured here
                  to verify test execution and device behavior.
                </p>
              </div>
            </div>
          </div>

          <WizardActions
            showBack={true}
            onBack={() => {
              previousStep();
              navigate('/app/onboarding?step=9');
            }}
            showNext={true}
            onNext={handleContinue}
            nextLabel="Continue"
            nextDisabled={!Object.values(evidenceSettings).some(val => val === true)}
          />
        </WizardCard>
      </WizardContainer>
    </PrivateWizardLayout>
  );
};

export default Step10IotValidation;